/**
 * The one place a settled payment turns into an owner notification.
 *
 * `payments.status = 'paid'` is written from several admin surfaces, and only
 * two of them used to tell the owner about it: the Reminders "Mark paid"
 * action (via recordManualRentPaidInLedger) and the PaymentsHub proof-gated
 * mark-paid. The tenant profile's Rent tab wrote the row directly — both from
 * its per-month row editor ("Mark paid") and from createScheduledRent's
 * "record this month as paid" — so the owner never got the "Mietzahlung
 * eingegangen" mail and info1@ never got the BCC.
 *
 * That also made the payment invisible on the owner's profile, not merely
 * unannounced: the Email history card is built strictly from real send rows in
 * `owner_lifecycle_emails` (see src/lib/owner-email-history.ts) and
 * `rent_received` renders straight from that log, so a missing send is a
 * missing entry rather than a pending one.
 *
 * Anything that flips a rent/deposit payment to paid from the client should
 * still call this — it is what puts the outcome on screen. It is no longer
 * what guarantees the mail: since 20260905120000 a DB trigger on `payments`
 * posts every transition to paid to owner-payment-email-tick via pg_net, so
 * the owner mail (and its info@ copy) goes out even when this invoke never
 * lands (dialog closed mid-flight, offline, 5xx). Both paths run through the
 * same stamp claim, so they cannot double-send. Server-side settlements
 * (stripe-webhook, confirm-payment-from-session) send from inside the function
 * that flips the row and never come through here.
 *
 * Best-effort on top of a committed ledger write, and idempotent — Send-
 * AndStampOwnerLifecycleEmail claims a per-row stamp before sending, so a
 * repeat call can never double-send. `owner-payment-email-tick` is the safety
 * net for a call that never lands at all (tab closed mid-flight, offline,
 * 5xx) — note that it only runs once its RUN_ME has been executed on the
 * project, which is a manual step per environment.
 *
 * It used to be fire-and-FORGET: `.catch(() => {})`, returning void. That is
 * what turned a skipped mail into a silent one. The invoke can come back
 * "muted", "no_recipient" or "send_failed", each meaning the owner heard
 * nothing, and discarding the answer meant the admin who clicked "Mark paid"
 * was told only that the payment was recorded. An owner went a month without a
 * rent receipt with every screen reporting success. So the outcome is returned
 * now; owner-payment-notify-report.ts is what puts it on screen, and callers
 * should use that rather than this function directly.
 */
import { supabase } from "@/integrations/supabase/client";

/** The columns notify-owner-lifecycle needs off a freshly-settled row. */
export type SettledPayment = {
  id: string;
  type?: string | null;
  booking_id?: number | null;
  amount_cents?: number | null;
  due_date?: string | null;
};

/**
 * What became of the owner's mail, for the surface that settled the payment.
 *
 * The point of returning anything at all: this used to be fire-and-forget with
 * an empty `.catch()`, so a muted preference, a missing address or a rejected
 * send produced no owner mail, no internal copy and no message on screen. The
 * admin clicked "Mark paid", saw "Rent recorded as paid", and had no way to
 * learn the owner was never told — which is exactly how a real owner went a
 * month without a rent receipt while everything looked fine.
 */
export type OwnerNotifyOutcome =
  | { status: "sent"; owner: string | null }
  /** A mail for this payment already went out; the stamp proves it. */
  | { status: "already_sent"; owner: string | null }
  | {
      status: "not_sent";
      /** SendAndStampOutcome.skipped — the machine reason. */
      reason: string;
      /** The same thing in words, ready for a toast. */
      detail: string;
      owner: string | null;
    }
  /** The call never landed, so nothing is known either way. */
  | { status: "unknown"; detail: string }
  /** Not a rent/deposit settlement — nothing was ever going to be sent. */
  | { status: "not_applicable" };

export async function notifyOwnerPaymentReceived(
  payment: SettledPayment | null | undefined
): Promise<OwnerNotifyOutcome> {
  const bookingId = payment?.booking_id;
  // A payment with no booking has no apartment, so no owner to notify.
  if (!payment || bookingId == null) return { status: "not_applicable" };

  const type = String(payment.type ?? "").toLowerCase();
  let body: Record<string, unknown> | null = null;
  if (type === "rent") {
    body = {
      event: "rent_received",
      bookingId,
      // Rent recurs monthly, so its idempotency stamp lives on the settled
      // payment row rather than on the booking — notify-owner-lifecycle
      // rejects the event outright without an id to stamp.
      paymentId: String(payment.id),
      amountCents: payment.amount_cents ?? null,
      dueDate: payment.due_date ?? null,
    };
  } else if (type === "deposit") {
    body = {
      event: "deposit_received",
      bookingId,
      amountCents: payment.amount_cents ?? null,
    };
  }
  if (!body) return { status: "not_applicable" };

  try {
    const { data, error } = await supabase.functions.invoke(
      "notify-owner-lifecycle",
      { body }
    );
    if (error) {
      return {
        status: "unknown",
        detail: error.message || String(error),
      };
    }
    return readOutcome(data as NotifyResponse | null);
  } catch (e) {
    // The ledger write has already committed. An unreachable function is a
    // notification problem, never a payment problem, so this resolves to an
    // "unknown" outcome instead of throwing into the caller's mutation.
    return {
      status: "unknown",
      detail: e instanceof Error ? e.message : String(e),
    };
  }
}

/** notify-owner-lifecycle's JSON, as far as this file cares. */
interface NotifyResponse {
  ok?: boolean;
  skipped?: string | null;
  error?: string | null;
  recipient?: { name?: string | null; email?: string | null } | null;
}

/**
 * Why the owner was not told, in words an admin can act on.
 *
 * Keyed on SendAndStampOutcome.skipped. Each one has a different fix, which is
 * the entire reason the reason is surfaced at all: "not delivered" sends
 * somebody hunting through logs, while "no email address on file" is a task.
 */
const NOT_SENT_REASON: Record<string, string> = {
  no_recipient: "no email address on file for this owner",
  muted: "the owner switched these notifications off",
  send_failed: "the mail provider rejected it",
};

function readOutcome(data: NotifyResponse | null): OwnerNotifyOutcome {
  const owner = ownerLabelOf(data?.recipient ?? null);
  const skipped = String(data?.skipped ?? "").trim();

  // `already_sent` is a success from the operator's point of view: a mail for
  // this exact payment has gone out before. The edge function only reports it
  // once it has checked the log for this exact row and confirmed the last
  // attempt actually was a send — a muted-owner retry comes back as `muted`
  // instead (see sendAndStampOwnerLifecycleEmail's `lastLoggedOutcome`), so
  // reporting it as a failure here would have an admin chase a delivered mail.
  if (skipped === "already_sent") return { status: "already_sent", owner };

  if (skipped) {
    return {
      status: "not_sent",
      reason: skipped,
      detail: NOT_SENT_REASON[skipped] ?? data?.error ?? "not delivered",
      owner,
    };
  }
  if (data?.ok) return { status: "sent", owner };

  // ok:false with no skip reason — the function rejected the request itself
  // (bad payload, booking gone, forbidden). Nothing was sent and nothing
  // classified it, so say exactly that rather than guessing.
  return { status: "unknown", detail: data?.error ?? "no response" };
}

function ownerLabelOf(
  recipient: { name?: string | null; email?: string | null } | null
): string | null {
  const name = recipient?.name?.trim();
  if (name) return name;
  const email = recipient?.email?.trim();
  return email || null;
}
